/**
 * ProjectEditDialog — 其他附件 Tab
 */
import React from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, PaperClipIcon, PlusIcon, TrashIcon, PencilSquareIcon, EyeIcon } from '@heroicons/react/24/outline';
import { ProjectAttachment } from '../types';
import { API_BASE_URL } from '../services/api';

const IMAGE_FILE_TYPES = ['image', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

interface ProjectAttachmentsTabProps {
  projectAttachments: ProjectAttachment[];
  projectAttLoading: boolean;
  uploadingProjectAtt: boolean;
  projectAttDesc: string;
  setProjectAttDesc: (v: string) => void;
  editingProjectAttDescId: number | null;
  editingProjectAttDescValue: string;
  setEditingProjectAttDescValue: (v: string) => void;
  setEditingProjectAttDescId: (id: number | null) => void;
  handleProjectAttUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProjectAttDescSave: (attId: number) => void;
  handleProjectAttDelete: (attId: number, fileName: string) => void;
  handleProjectAttEditText: (att: ProjectAttachment) => void;
  setProjectAttPreviewUrl: (url: string | null) => void;
  projectAttTextLimit?: number;
}

export default function ProjectAttachmentsTab({
  projectAttachments, projectAttLoading, uploadingProjectAtt,
  projectAttDesc, setProjectAttDesc,
  editingProjectAttDescId, editingProjectAttDescValue,
  setEditingProjectAttDescValue, setEditingProjectAttDescId,
  handleProjectAttUpload, handleProjectAttDescSave,
  handleProjectAttDelete, handleProjectAttEditText,
  setProjectAttPreviewUrl,
  projectAttTextLimit = 2000,
}: ProjectAttachmentsTabProps) {
  const limit = projectAttTextLimit || 2000;
  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-500">
        上传项目相关附件（如投标保证金凭证、其他材料等），可添加文件描述便于 LLM 理解
      </div>
      <div className="flex items-center gap-2">
        <input type="text" value={projectAttDesc} onChange={(e) => setProjectAttDesc(e.target.value)}
          placeholder="文件描述（便于 LLM 理解文件内容），如：投标保证金"
          className="flex-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          maxLength={500} />
        <label className={`inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-colors flex-shrink-0 ${uploadingProjectAtt ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
          <input type="file" className="hidden"
            accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.docx,.doc,.txt,.md,.markdown"
            multiple
            disabled={uploadingProjectAtt} onChange={(e) => handleProjectAttUpload(e)} />
          <PlusIcon className="w-3 h-3 mr-0.5" />
          {uploadingProjectAtt ? '上传中...' : '上传文件'}
        </label>
      </div>
      {projectAttLoading ? (
        <div className="text-center py-6 text-xs text-gray-400">加载中...</div>
      ) : projectAttachments.length === 0 ? (
        <div className="text-center py-8 text-xs text-gray-400">
          <PaperClipIcon className="mx-auto h-8 w-8 text-gray-300 mb-1" />
          暂无附件，请填写文件描述后上传
        </div>
      ) : (
        <div className="space-y-1.5">
          {projectAttachments.map(att => (
            <div key={att.id} className="border border-gray-200 rounded-lg p-2.5 bg-white">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <PaperClipIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-gray-700 truncate">{att.file_name}</span>
                  </div>
                  <span className="text-[10px] text-gray-400 ml-5">
                    {att.file_type.toUpperCase()} · {att.file_size > 1024 ? `${(att.file_size / 1024).toFixed(1)} KB` : `${att.file_size} B`}
                    {att.extracted_text_length > 0 && (
                      <span className={`ml-2 inline-flex items-center gap-0.5 ${att.extracted_text_length > limit ? 'text-red-500 font-medium' : 'text-green-500'}`}>
                        {att.extracted_text_length > limit ? <ExclamationCircleIcon className="w-3 h-3" /> : <CheckCircleIcon className="w-3 h-3" />}
                        提取 {att.extracted_text_length} 字符
                        {att.extracted_text_length > limit && <span className="text-[10px] text-red-400">（超限，仅前{limit}字符送入LLM）</span>}
                      </span>
                    )}
                  </span>
                  <div className="mt-1 ml-5">
                    {editingProjectAttDescId === att.id ? (
                      <div className="flex items-center gap-1">
                        <input type="text" value={editingProjectAttDescValue}
                          onChange={(e) => setEditingProjectAttDescValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleProjectAttDescSave(att.id); if (e.key === 'Escape') setEditingProjectAttDescId(null); }}
                          className="flex-1 px-2 py-1 text-xs border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          maxLength={500} autoFocus />
                        <button onClick={() => handleProjectAttDescSave(att.id)} className="px-1.5 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700">保存</button>
                        <button onClick={() => setEditingProjectAttDescId(null)} className="px-1.5 py-0.5 text-[10px] text-gray-500 hover:text-gray-700">取消</button>
                      </div>
                    ) : att.description ? (
                      <div className="flex items-center gap-1 group">
                        <span className="text-xs text-gray-600 truncate">描述：{att.description}</span>
                        <button onClick={() => { setEditingProjectAttDescId(att.id); setEditingProjectAttDescValue(att.description || ''); }}
                          className="p-0.5 rounded text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">
                          <PencilSquareIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingProjectAttDescId(att.id); setEditingProjectAttDescValue(''); }}
                        className="text-xs text-gray-400 hover:text-blue-500 transition-colors">+ 添加文件描述</button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0 mt-0.5">
                  {IMAGE_FILE_TYPES.includes(att.file_type) && att.file_url ? (
                    <button onClick={() => setProjectAttPreviewUrl(API_BASE_URL + att.file_url)}
                      className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors" title="预览图片">
                      <EyeIcon className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button onClick={() => handleProjectAttEditText(att)}
                      className="p-1 rounded text-gray-400 hover:text-green-500 hover:bg-green-50 transition-colors" title="编辑内容">
                      <PencilSquareIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => handleProjectAttDelete(att.id, att.file_name)}
                    className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
